import React, { memo } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook, Twitter, Linkedin } from 'lucide-react';
import { companyInfo, services } from '../data/mock';

export const Footer = memo(() => {
  return (
    <footer className="bg-[#0f2744] text-white">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div>
            <img 
              src="https://customer-assets.emergentagent.com/job_mouldsurv-essex/artifacts/ux9ghpyg_20601.png" 
              alt="Mouldsurv Ltd" 
              className="h-20 w-auto mb-4 brightness-0 invert"
            />
            <p className="text-gray-300 text-sm mb-4">
              {companyInfo.tagline}
            </p>
            <div className="flex gap-3">
              <a href={companyInfo.socialMedia.facebook} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 hover:bg-[#4a7c59] flex items-center justify-center transition-colors">
                <Facebook size={18} />
              </a>
              <a href={companyInfo.socialMedia.instagram} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 hover:bg-[#4a7c59] flex items-center justify-center transition-colors">
                <Twitter size={18} />
              </a>
              <a href={companyInfo.socialMedia.linkedin} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 hover:bg-[#4a7c59] flex items-center justify-center transition-colors">
                <Linkedin size={18} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-[#5d9e5f]">Quick Links</h3>
            <ul className="space-y-2">
              <li><Link to="/" className="text-gray-300 hover:text-[#5d9e5f] transition-colors text-sm">Home</Link></li>
              <li><Link to="/services" className="text-gray-300 hover:text-[#5d9e5f] transition-colors text-sm">Our Services</Link></li>
              <li><Link to="/gallery" className="text-gray-300 hover:text-[#5d9e5f] transition-colors text-sm">Gallery</Link></li>
              <li><Link to="/blog" className="text-gray-300 hover:text-[#5d9e5f] transition-colors text-sm">Blog</Link></li>
              <li><Link to="/legislation" className="text-gray-300 hover:text-[#5d9e5f] transition-colors text-sm">UK Legislation</Link></li>
              <li><Link to="/contact" className="text-gray-300 hover:text-[#5d9e5f] transition-colors text-sm">Contact Us</Link></li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-[#5d9e5f]">Policies & Legal</h3>
            <ul className="space-y-2">
              <li><Link to="/quality-policy" className="text-gray-300 hover:text-[#5d9e5f] transition-colors text-sm">Quality Policy</Link></li>
              <li><Link to="/qms-scope" className="text-gray-300 hover:text-[#5d9e5f] transition-colors text-sm">QMS Scope</Link></li>
              <li><Link to="/compliance-improvement" className="text-gray-300 hover:text-[#5d9e5f] transition-colors text-sm">Compliance</Link></li>
              <li><Link to="/privacy-policy" className="text-gray-300 hover:text-[#5d9e5f] transition-colors text-sm">Privacy Policy</Link></li>
              <li><Link to="/gdpr-policy" className="text-gray-300 hover:text-[#5d9e5f] transition-colors text-sm">GDPR Policy</Link></li>
              <li><Link to="/terms-conditions" className="text-gray-300 hover:text-[#5d9e5f] transition-colors text-sm">Terms & Conditions</Link></li>
              <li><Link to="/complaints-procedure" className="text-gray-300 hover:text-[#5d9e5f] transition-colors text-sm">Complaints</Link></li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-[#5d9e5f]">Contact Us</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3 text-sm">
                <MapPin size={18} className="text-[#5d9e5f] flex-shrink-0 mt-0.5" />
                <span className="text-gray-300">{companyInfo.address}</span>
              </li>
              <li className="flex items-center gap-3 text-sm">
                <Phone size={18} className="text-[#5d9e5f] flex-shrink-0" />
                <a href={`tel:${companyInfo.phone}`} className="text-gray-300 hover:text-[#5d9e5f] transition-colors">
                  {companyInfo.phone}
                </a>
              </li>
              <li className="flex items-center gap-3 text-sm">
                <Mail size={18} className="text-[#5d9e5f] flex-shrink-0" />
                <a href={`mailto:${companyInfo.email}`} className="text-gray-300 hover:text-[#5d9e5f] transition-colors">
                  {companyInfo.email}
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 mt-12 pt-8">
          <div className="text-center mb-6">
            <p className="text-gray-300 text-sm mb-2">
              Mouldsurv is a trading name of <strong className="text-white">Oxford Environmental Consultants Limited</strong>
            </p>
            <p className="text-gray-400 text-xs mb-1">
              Company number 11540812
            </p>
            <p className="text-gray-400 text-xs">
              Suite 1A, Unit 5 Wickham Business Park, Honywood Road, Basildon, Essex, SS14 3EQ
            </p>
          </div>
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-400 text-sm text-center md:text-left">
              © {new Date().getFullYear()} {companyInfo.name}. All rights reserved.
            </p>
            <div className="flex gap-6 text-sm">
              <Link to="/privacy" className="text-gray-400 hover:text-[#5d9e5f] transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="text-gray-400 hover:text-[#5d9e5f] transition-colors">Terms of Service</Link>
              <Link to="/staff/login" className="text-gray-400 hover:text-[#5d9e5f] transition-colors opacity-50">Staff</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
});
